import { Layer, Polygon, type UserDetails } from '../../context/context';
import { AgentService, CreateJiraTicketResult } from './agentService';
import { generateKMLFromLayer } from './fileService';

type SpatialAnalysisTicketInput = {
  polygon: Polygon;
  layerName?: string;
  currentUrl?: string;
  submitter?: UserDetails;
  agentService?: AgentService;
};

const SPATIAL_ANALYSIS_PROJECT_KEY = 'CAB';
const SPATIAL_ANALYSIS_ISSUE_TYPE = 'Spatial Analysis';

function sanitizeFileName(value: string): string {
  return (
    value
      .trim()
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'polygon'
  );
}

function buildSinglePolygonLayer(polygon: Polygon, layerName?: string): Layer {
  return {
    layerId: polygon.layerId ?? 0,
    layerName: layerName || polygon.name || 'Spatial Analysis Polygon',
    hidden: false,
    shapes: [polygon],
  };
}

export function buildSpatialAnalysisTicketDescription({
  polygon,
  layerName,
  currentUrl,
  submitter,
}: {
  polygon: Polygon;
  layerName?: string;
  currentUrl?: string;
  submitter?: UserDetails;
}): string {
  const outerRing = polygon.coordinates[0] ?? [];
  const coordinateRows = outerRing
    .map(([lat, lng], index) => `${index + 1}. ${lat}, ${lng}`)
    .join('\n');
  const submitterName = submitter?.fullName?.trim() || submitter?.email?.trim() || 'Unknown';
  const submitterEmail = submitter?.email?.trim() || 'Unknown';
  const submitterUserId = submitter?.userId?.trim() || 'Unknown';

  return [
    'Spatial analyst ticket created from a right-clicked Atlas polygon.',
    '',
    '### Submitted By',
    `- Name: ${submitterName}`,
    `- Email: ${submitterEmail}`,
    `- User ID: ${submitterUserId}`,
    '',
    '### Polygon',
    `- Name: ${polygon.name || 'Unnamed polygon'}`,
    `- Layer: ${layerName || 'Unknown layer'}`,
    `- Polygon ID: ${polygon.id}`,
    `- Vertex count: ${outerRing.length}`,
    currentUrl ? `- Source map: ${currentUrl}` : null,
    '',
    '### Requested Analysis',
    '- Run the standard spatial analysis workflow for this polygon.',
    '- Use the attached KML as the service-area boundary.',
    '',
    '### Polygon Coordinates',
    coordinateRows || 'No coordinates were available on the selected polygon.',
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
}

export async function createSpatialAnalysisTicket({
  polygon,
  layerName,
  currentUrl,
  submitter,
  agentService = new AgentService(),
}: SpatialAnalysisTicketInput): Promise<CreateJiraTicketResult> {
  const layer = buildSinglePolygonLayer(polygon, layerName);
  const kml = generateKMLFromLayer(layer);
  const fileName = `${sanitizeFileName(polygon.name || layer.layerName)}-spatial-analysis.kml`;
  const kmlFile = new File([kml], fileName, {
    type: 'application/vnd.google-earth.kml+xml',
  });
  const uploaded = await agentService.uploadFile(kmlFile);
  const title = `[Spatial Analysis] ${polygon.name || layer.layerName || 'Polygon'}`;
  const description = buildSpatialAnalysisTicketDescription({
    polygon,
    layerName: layer.layerName,
    currentUrl,
    submitter,
  });

  return agentService.createJiraTicket(
    title,
    description,
    SPATIAL_ANALYSIS_PROJECT_KEY,
    SPATIAL_ANALYSIS_ISSUE_TYPE,
    [uploaded.file_id]
  );
}
